import { mockComponents } from '@/lib/mock-data-components';
import { mockDisplayComponents } from '@/lib/mock-data-components';
import { mockDisplayTypes } from '@/lib/mock-data-components';
import type { ComponentPricing, ComponentPricingInsert, ComponentPricingUpdate } from '@/types/component-pricing';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// In-memory pricing storage (initially populated from component default_cost)
let mockComponentPricing: ComponentPricing[] = mockComponents.map((comp) => ({
  id: `pricing-${comp.id}`,
  component_id: comp.id,
  price: comp.default_cost || 0,
  vendor: comp.default_vendor,
  vendor_url: null,
  last_synced_at: null,
  synced_from_sheet: false,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
}));

export const mockComponentPricingService = {
  async getAll(): Promise<ComponentPricing[]> {
    await delay(200);
    return [...mockComponentPricing];
  },

  async getByComponentId(componentId: string): Promise<ComponentPricing | null> {
    await delay(200);
    return mockComponentPricing.find((p) => p.component_id === componentId) ?? null;
  },

  async create(data: ComponentPricingInsert): Promise<ComponentPricing> {
    await delay(300);
    const created: ComponentPricing = {
      id: `pricing-${data.component_id}`,
      component_id: data.component_id,
      price: data.price,
      vendor: data.vendor ?? null,
      vendor_url: data.vendor_url ?? null,
      last_synced_at: data.last_synced_at ?? null,
      synced_from_sheet: data.synced_from_sheet ?? false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockComponentPricing = mockComponentPricing.filter((p) => p.component_id !== data.component_id);
    mockComponentPricing.push(created);
    return created;
  },

  async updatePrice(componentId: string, price: number): Promise<ComponentPricing> {
    await delay(300);
    const index = mockComponentPricing.findIndex((p) => p.component_id === componentId);
    if (index === -1) {
      throw new Error('Component pricing not found');
    }
    const existing = mockComponentPricing[index];
    if (!existing) {
      throw new Error('Component pricing not found');
    }
    const updated: ComponentPricing = {
      ...existing,
      price,
      updated_at: new Date().toISOString(),
    };
    mockComponentPricing[index] = updated;
    
    // Also update the component's default_cost for consistency
    const componentIndex = mockComponents.findIndex((c) => c.id === componentId);
    if (componentIndex !== -1) {
      const component = mockComponents[componentIndex];
      if (component) {
        component.default_cost = price;
      }
    }
    
    return updated;
  },

  async update(componentId: string, data: ComponentPricingUpdate): Promise<ComponentPricing> {
    await delay(300);
    const index = mockComponentPricing.findIndex((p) => p.component_id === componentId);
    if (index === -1) {
      throw new Error('Component pricing not found');
    }
    const existing = mockComponentPricing[index];
    if (!existing) {
      throw new Error('Component pricing not found');
    }
    const updated: ComponentPricing = {
      ...existing,
      ...data,
      updated_at: new Date().toISOString(),
    };
    mockComponentPricing[index] = updated;
    return updated;
  },

  async delete(componentId: string): Promise<void> {
    await delay(200);
    mockComponentPricing = mockComponentPricing.filter((p) => p.component_id !== componentId);
  },

  async getAffectedDisplayTypes(componentId: string): Promise<Array<{ id: string; name: string }>> {
    await delay(200);
    const displayComponents = mockDisplayComponents.filter((dc) => dc.component_id === componentId);
    const displayTypeIds = [...new Set(displayComponents.map((dc) => dc.display_type_id))];
    return displayTypeIds
      .map((id) => {
        const dt = mockDisplayTypes.find((d) => d.id === id);
        return dt ? { id: dt.id, name: dt.name } : null;
      })
      .filter((dt): dt is { id: string; name: string } => dt !== null);
  },

  async getPriceForComponent(componentId: string): Promise<number> {
    await delay(200);
    // First, try to get price from component_pricing
    const pricing = await this.getByComponentId(componentId);
    if (pricing && pricing.price) {
      return pricing.price;
    }

    // Fall back to component.default_cost
    const component = mockComponents.find((c) => c.id === componentId);
    if (component && component.default_cost !== null) {
      return component.default_cost;
    }

    // If neither exists, throw an error
    throw new Error(`No price found for component ${componentId}`);
  },
};
